import { FetchBoundary } from "providers/FetchBoundary";
import { useNavigate } from "react-router-dom";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { ArrowLeft, Clock } from "untitledui-js-base";
import { ApplicantRadioCard } from "./ApplicantRadioCard";
import { FC, useState } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { Application } from "types/application";
import {
  ApplicantStatus,
  ApplicantType,
  ApplicationStatus
} from "shared/types";
import { ActivityLog } from "shared/app/ActivityLog";
import { MakeDecisionModal } from "shared/app/MakeDecisionModal";
import { useAuthContext } from "providers/Auth";
import { getFullName } from "shared/utils/get-full-name";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import {
  canDecisionBeMade,
  canSeeDecisionButton,
  getUnableToMakeDecisionReason
} from "utils/canMakeDecision";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { getInviteApplicationDisabledToolTip } from "views/applications-id/components/LayoutHeader/helpers";
import { ViewDiscountCodesButton } from "views/discounts/components/ViewDiscountCodesButton";

export const LayoutHeader: FC<{
  setSearchParams: (newState: Record<string, string>) => void;
  searchParams: Record<string, string>;
  applicationMeta: UseQueryResult<Application | undefined, unknown>;
  templateId: string;
}> = ({ applicationMeta, searchParams, setSearchParams, templateId }) => {
  const navigate = useNavigate();
  const { permissions, type } = useAuthContext();
  const applicationStatus = applicationMeta.data?.status;
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMakeDecisionModal, setShowMakeDecisionModal] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);

  const canMakeDecision = canDecisionBeMade({
    applicants: applicationMeta.data?.applicants || [],
    status: applicationStatus || ApplicationStatus.Started,
    type,
    permissions,
    requirements: applicationMeta.data?.make_decision_requirements
  });
  const cannotMakeDecisionText = !canMakeDecision
    ? getUnableToMakeDecisionReason({
        requirements: applicationMeta.data?.make_decision_requirements
      })
    : undefined;

  const isMakeDecisionVisible = canSeeDecisionButton({
    permissions,
    type,
    status: applicationStatus || ApplicationStatus.Started
  });

  const isInviteDisabled = !(
    [
      ApplicationStatus.Started,
      ApplicationStatus.Submitted,
      ApplicationStatus.Pending
    ] as string[]
  ).includes(applicationStatus || "");

  return (
    <div className="no-print bg-white">
      <div className="flex justify-between border-b border-alpha-40 p-3 md:px-6 md:py-4 md:flex-nowrap flex-wrap gap-2">
        <div className="flex items-center gap-2 md:gap-6 w-full ">
          <Button
            size={ButtonSize.SM}
            Icon={ArrowLeft}
            onClick={() => {
              navigate(-1);
            }}
          >
            Back
          </Button>
          <h1 className="text-base font-bold text-content-1000">Applicants</h1>

          <div className="ml-auto md:hidden">
            <ContextMenuPopover
              actions={
                isMakeDecisionVisible
                  ? [
                      {
                        title: "Invite applicant",
                        onClick: () => setShowInviteModal(true),
                        disabled: isInviteDisabled,
                        tooltip:
                          getInviteApplicationDisabledToolTip(applicationStatus)
                      },
                      {
                        title: "Make Decision",
                        onClick: () => setShowMakeDecisionModal(true),
                        disabled: !canMakeDecision,
                        tooltip: cannotMakeDecisionText
                      },
                      {
                        title: "Activity log",
                        onClick: () => setShowActivityLog(true)
                      }
                    ]
                  : [
                      {
                        title: "Invite applicant",
                        onClick: () => setShowInviteModal(true),
                        disabled: isInviteDisabled,
                        tooltip:
                          getInviteApplicationDisabledToolTip(applicationStatus)
                      },
                      {
                        title: "Activity log",
                        onClick: () => setShowActivityLog(true)
                      }
                    ]
              }
            />
          </div>
        </div>

        <div className="gap-2 hidden md:flex">
          <ViewDiscountCodesButton templateId={templateId} />
          <Button
            borderLess
            className="!text-content-400 !stroke-content-400"
            size={ButtonSize.SM}
            variant={ButtonVariant.BASE}
            Icon={Clock}
            onClick={() => {
              setShowActivityLog(true);
            }}
          >
            Activity log
          </Button>
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={() => setShowInviteModal(true)}
            disabled={isInviteDisabled}
            tooltip={getInviteApplicationDisabledToolTip(applicationStatus)}
          >
            Invite applicant
          </Button>

          <Show if={isMakeDecisionVisible}>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              onClick={() => setShowMakeDecisionModal(true)}
              disabled={!canMakeDecision}
              tooltip={cannotMakeDecisionText}
            >
              Make Decision
            </Button>
          </Show>
        </div>

        {applicationMeta.data && showMakeDecisionModal && (
          <MakeDecisionModal
            opened={showMakeDecisionModal}
            onClose={() => {
              applicationMeta.refetch();
              setShowMakeDecisionModal(false);
            }}
            applicationId={String(applicationMeta.data.id)}
          />
        )}

        {showInviteModal && (
          <InviteAndCreateModal
            disabledRoles={
              [
                ...(applicationMeta.data?.applicants || []),
                ...(applicationMeta.data?.pending_magic_links || [])
              ].some((applicant) =>
                [ApplicantType.Applicant, ApplicantType.Occupant].includes(
                  applicant.role as ApplicantType
                )
              )
                ? []
                : [ApplicantType.CoSigner]
            }
            applicationId={Number(applicationMeta.data?.id)}
            open={showInviteModal}
            setOpen={() => setShowInviteModal(false)}
            propertyId={applicationMeta.data?.property.id ?? 0}
            unitId={applicationMeta.data?.property.id ?? 0}
            propertyName={applicationMeta.data?.property.name}
            unitName={applicationMeta.data?.unit.title}
            onInviteRefreshKey={[
              "APPLICATION_ID_REPORT",
              String(applicationMeta.data?.id)
            ]}
          />
        )}
      </div>

      <div className="grid w-full grid-flow-row auto-cols-fr grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
        <FetchBoundary
          loading={applicationMeta.isLoading || !applicationMeta.data}
          error={applicationMeta.error}
        >
          {applicationMeta.data
            ? applicationMeta.data.applicants.map((app) => {
                const checked =
                  String(app.id) === String(searchParams.applicantId);
                return (
                  <ApplicantRadioCard
                    applicant={app}
                    key={app.id}
                    onClick={() => {
                      document
                        .getElementById("report_section")
                        ?.scrollTo({ top: 0, behavior: "smooth" });

                      if (!checked) {
                        const verificationToNavigateTo = searchParams.report;

                        const isReportPresentInApplicantVerifications =
                          app.verifications.some(
                            (v) => v.name === verificationToNavigateTo
                          );
                        if (isReportPresentInApplicantVerifications)
                          return setSearchParams({
                            ...searchParams,
                            applicantId: String(app.id)
                          });

                        return setSearchParams({
                          ...searchParams,
                          report: "rental_application",
                          applicantId: String(app.id)
                        });
                      }
                    }}
                    fullName={getFullName({
                      firstName: app.first_name,
                      lastName: app.last_name,
                      middleName: app.middle_name,
                      short: true
                    })}
                    role={app.role}
                    checked={checked}
                    isSubmitted={
                      app.application_status === ApplicantStatus.Submitted
                    }
                    id={String(app.id)}
                  />
                );
              })
            : null}
        </FetchBoundary>
      </div>

      {/* Show removes unnecessary fetch request */}
      <Show if={showActivityLog}>
        <ActivityLog
          showActivityLog={showActivityLog}
          setShowActivityLog={setShowActivityLog}
          applicationId={String(applicationMeta.data?.id) || ""}
        />
      </Show>
    </div>
  );
};
